import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IBwjAnnouncementBannerProps, IShowAnnouncementBannerArgs } from './props';
import { DefaultButton, FontIcon, getTheme, IconButton, IStyle, ITheme, mergeStyleSets, MessageBarType } from '@fluentui/react';
import { isNotNullOrWhitespace } from '../../utils';
import { PRIVATE_ANNOUNCEMENT_DIALOG } from './dialog';
import { getBwjAnnouncementBannerColorScheme } from './color';
import $ from 'jquery';

const MESSAGE_LENGTH_POLLING_INTERVAL = 1200; // milliseconds

const getStyleClasses = (theme: ITheme, type: MessageBarType) => {
  const colors = getBwjAnnouncementBannerColorScheme(type);

  const main: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: theme.fonts.medium.fontSize,
    padding: '0.5em',
    backgroundColor: colors.background,
    borderBottom: `1px solid ${colors.main}`,
  };

  const icon: IStyle = {
    padding: '0.33333333333333em',
    fontSize: '1.5em',
    color: colors.main,
  };

  const messageContainer: IStyle = {
    margin: 0,
    padding: '0.5em',
    width: 1,
    flexGrow: 1,
    position: 'relative',
  };

  const messageBase: IStyle = {
    margin: 0,
    padding: 0,
    lineHeight: '1.5em',
    width: 'auto',
    whiteSpace: 'nowrap',
  };

  const message: IStyle = {
    ...messageBase,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const messageRuler: IStyle = {
    ...messageBase,
    display: 'inline-block',
    position: 'absolute',
    top: -1000,
    opacity: 0,
  };

  const moreButtonContainer: IStyle = {
    padding: '0.5em',
    paddingLeft: 0,
  };

  const closeButtonContainer: IStyle = {
    padding: '0.5em',
  };

  return mergeStyleSets({
    main,
    icon,
    messageContainer,
    message,
    messageRuler,
    moreButtonContainer,
    closeButtonContainer,
  });
};

const getIconForType = (args: IShowAnnouncementBannerArgs | undefined) => {
  if(args === undefined) { return undefined; }

  // developer explicitly overriding default
  if(args.icon !== undefined) {
    return isNotNullOrWhitespace(args.icon) ? args.icon : undefined;
  }

  switch(args.messageType) {
    case MessageBarType.error: return 'ErrorBadge';
    case MessageBarType.blocked: return 'BlockedSite';
    case MessageBarType.warning: return 'Warning';
    case MessageBarType.severeWarning: return 'WarningSolid';
    case MessageBarType.success: return 'SkypeCircleCheck';
    default: return 'Info';
  }
};

export const BwjAnnouncementBanner: React.FunctionComponent<IBwjAnnouncementBannerProps> = (props) => {
  const theme = getTheme();
  const [showBanner, setShowBanner] = useState(false);
  const [showDisplayFullMessageButton, setShowDisplayFullMessageButton] = useState(false);
  const { invokeShowAnnouncement: bannerProps, linkHandler } = props;
  const [invokeShowModal, setInvokeModal] = useState<IShowAnnouncementBannerArgs | undefined>(undefined);
  const styles = useMemo(() => getStyleClasses(theme, bannerProps?.messageType ?? MessageBarType.info), [theme, bannerProps]);
  const icon = getIconForType(bannerProps);
  const messageContainerRef = useRef<HTMLDivElement | null>(null);
  const messageRulerRef = useRef<HTMLParagraphElement | null>(null);
  const messageRef = useRef<HTMLParagraphElement | null>(null);
  const processCancelRef = useRef<{ id: number, timeout: boolean } | undefined>(undefined);
  const showFullMessage = props.ariaShowFullMessageDialog ?? 'Display full message';
  const closeAnnouncement = props.ariaCloseBannerMessage ?? 'Hide message';

  const startSizeMonitoring = useCallback(() => {
    const monitorSize = () => {
      const msgContainer = messageContainerRef.current;
      const msgRuler = messageRulerRef.current;
      if(msgContainer === null || msgRuler === null) { return; }

      const showBtn = msgRuler.clientWidth > msgContainer.clientWidth;
      setShowDisplayFullMessageButton(showBtn);
    }

    processCancelRef.current = { id: 0, timeout: true };
    processCancelRef.current.id = window.setTimeout(() => {
      monitorSize();
      processCancelRef.current!.timeout = false;
      processCancelRef.current!.id = window.setInterval(monitorSize, MESSAGE_LENGTH_POLLING_INTERVAL);
    }, 100);
  }, []);

  const endSizeMonitoring = useCallback(() => {
    if(processCancelRef.current === undefined) { return; }

    const process = processCancelRef.current;
    if(process.timeout) {
      window.clearTimeout(process.id);
    } else {
      window.clearInterval(process.id);
    }

    setShowDisplayFullMessageButton(false);
    processCancelRef.current = undefined;
  }, []);

  // on show banner invoked
  useEffect(() => {
    if(bannerProps === undefined) { return; }
    setShowBanner(true);
    startSizeMonitoring();

    return () => endSizeMonitoring();
  }, [bannerProps, startSizeMonitoring, endSizeMonitoring]);

  const onShowFullMessage = () => {
    const args = {...bannerProps!};
    args.icon = icon;
    setInvokeModal(args);
  };

  // on every redraw, wire up link event handlers if needed
  useEffect(() => {
    if(messageRef.current === null) { return; }

    const msg = messageRef.current;
    if(!!linkHandler) {
      $(msg).children('a').on('click', (e) => {
        linkHandler($(e.target).attr('href') ?? '#');
        e.stopPropagation();
        e.preventDefault();
      });
    }

    return () => {
      if(!!linkHandler) {
        $(msg).children('a').off('click');
      }
    };
  });

  const onDismiss = () => {
    setShowBanner(false);
    endSizeMonitoring();
    !!props.onDismiss && props.onDismiss();
  }

  return (
    <>
      {showBanner && !!bannerProps &&
      <section className={styles.main}>
        <PRIVATE_ANNOUNCEMENT_DIALOG
          invokeShowAnnouncement={invokeShowModal}
          linkHandler={props.linkHandler}
          closeDialogButtonText={props.closeDialogButtonText}
        />
        {!!icon &&
        <FontIcon iconName={icon} className={styles.icon} />}
        <div
          className={styles.messageContainer}
        >
          <div ref={messageContainerRef}>
            <p
              aria-hidden
              ref={messageRulerRef}
              className={styles.messageRuler}
              dangerouslySetInnerHTML={bannerProps.renderAsHtml === true ? { __html: bannerProps.message } : undefined}
            >
              {bannerProps.renderAsHtml !== true ? bannerProps.message : null}
            </p>
            <p
              ref={messageRef}
              className={styles.message}
              dangerouslySetInnerHTML={bannerProps.renderAsHtml === true ? { __html: bannerProps.message } : undefined}
            >
              {bannerProps.renderAsHtml !== true ? bannerProps.message : null}
            </p>
          </div>
        </div>
        <div
          className={styles.moreButtonContainer}
          style={{ visibility: showDisplayFullMessageButton ? 'visible' : 'hidden' }}
        >
          <DefaultButton
            styles={{
              root: {
                height: '1.5em !important',
                padding: '0.75em',
                minWidth: 1,
              },
            }}
            title={showFullMessage}
            ariaLabel={showFullMessage}
            iconProps={{ iconName: 'More' }}
            onClick={onShowFullMessage}
          />
        </div>
        {bannerProps.dismissable &&
        <div className={styles.closeButtonContainer}>
          <IconButton
            ariaLabel={closeAnnouncement}
            iconProps={{ iconName: 'ChromeClose' }}
            styles={{
              root: {
                height: '1.5em !important',
              },
            }}
            onClick={onDismiss}
          />
        </div>}
      </section>}
    </>
  );
};
